import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { MagnifyingGlass, Target, PuzzlePiece, CurrencyInr, Handshake, RocketLaunch } from "@phosphor-icons/react";

const STEP_ICONS = [MagnifyingGlass, Target, PuzzlePiece, CurrencyInr, Handshake, RocketLaunch];

export const NEXT_RE = /```next\s*\n([\s\S]*?)```/;
export const PRACTICE_START = "PRACTICE THE CLIENT CONVERSATION";

export function quickReplyFor(content) {
  const next = content.match(NEXT_RE);
  if (next) return next[1].trim();
  if (/Practice complete/i.test(content)) return null;
  if (/Same six steps\. Every problem\. Every time\./i.test(content)) return "Practice the client conversation";
  if (/LET'S SOLVE/i.test(content)) return "LET'S SOLVE";
  return null;
}

export function StepsCard({ text }) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const current = Number((lines.find((l) => /^current:/i.test(l)) || "").replace(/current:\s*/i, "")) || 1;
  const steps = lines.map((l) => l.match(/^(\d)\.\s+(.+?)\s+[—–-]\s+(.+)$/)).filter(Boolean);
  return (
    <div className="steps-card" data-testid="steps-card">
      <div className="steps-card-head">
        <span>Your consulting roadmap</span>
        <span className="steps-card-badge" data-testid="steps-card-badge">step {current}/{steps.length || 6}</span>
      </div>
      {steps.map(([, n, title, desc]) => {
        const i = Number(n);
        const Icon = STEP_ICONS[i - 1] || Target;
        const state = i < current ? "is-done" : i === current ? "is-current" : "";
        return (
          <div key={n} className={`steps-row ${state}`} data-testid={`steps-row-${n}`}>
            <span className="steps-num">{i < current ? <Check size={13} /> : <Icon size={15} weight="bold" />}</span>
            <div>
              <p className="steps-title">{n}. {title}</p>
              <p className="steps-desc">{desc}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function useCopy(text) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return [copied, copy];
}

export function YouCard({ text }) {
  const lines = text.split("\n");
  const hasLabel = lines.length > 1 && lines[0].trim().length <= 40 && lines[0].trim() === lines[0].trim().toUpperCase();
  const label = hasLabel ? lines[0].trim() : "YOU SAY";
  const body = (hasLabel ? lines.slice(1) : lines).join("\n").trim();
  const [copied, copy] = useCopy(body);
  return (
    <div className="you-card" data-testid="you-card">
      <div className="you-card-head">
        <span className="you-card-label">{label}</span>
        <button type="button" className={`markdown-copy-btn is-inline ${copied ? "is-copied" : ""}`} onClick={copy} aria-label="Copy" data-testid="you-card-copy">
          {copied ? <Check size={13} /> : <Copy size={13} />}<span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
      <div className="you-card-body">{body.split(/\n\s*\n/).map((p, i) => <p key={i}>{p.trim()}</p>)}</div>
    </div>
  );
}

export function ClientCard({ text }) {
  const [head, ...rest] = text.split("\n");
  const [name, title] = head.split(/\s+[—–-]\s+/);
  const body = rest.join("\n").trim();
  return (
    <div className="client-card" data-testid="client-card">
      <div className="client-card-head">
        <span className="client-avatar">{(name || "?").trim().charAt(0).toUpperCase()}</span>
        <div>
          <p className="client-name">{(name || "").trim()}</p>
          {title && <p className="client-title">{title.trim()}</p>}
        </div>
      </div>
      <div className="client-card-body">{body.split(/\n\s*\n/).map((p, i) => <p key={i}>{p.trim()}</p>)}</div>
    </div>
  );
}
